import React, { useEffect, useState } from "react";
import {
  Card,
  Descriptions,
  Spin,
  Button,
  Drawer,
  Form,
  Input,
} from "antd";
import { toastError, toastSuccess } from "../../../utils/toast";
import { fetchProfile as apiFetchProfile, updateProfile as apiUpdateProfile } from "../../../api/profileApi";
import { BASE_URL } from "../../../config/env";

export default function AdminProfilePage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form] = Form.useForm();

  const loadProfile = async () => {
    try {
      const localUser = JSON.parse(localStorage.getItem("user"));
      if (!localUser || !localUser.token) {
        throw new Error("User not logged in");
      }

      const res = await apiFetchProfile(localUser.id);

      if (res.status_code !== 200) {
        throw new Error(res.message || "Failed to load profile");
      }

      setUser(res.data);
    } catch (error) {
      toastError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (values) => {
    try {
      const res = await apiUpdateProfile(user.id, values);
      const payload = res?.data ?? res;
  
      if (res?.status_code === 200) {
        const prev = JSON.parse(localStorage.getItem("user")) || {};
        const d = payload.data || {};
        const normalizeImage = (img) => {
          if (!img) return null;
          if (/^https?:\/\//i.test(img)) return img; // already absolute
          const base = String(BASE_URL).replace(/\/+$/, "");      // trim trailing slashes
          const path = String(img).replace(/^\/+/, "");           // trim leading slashes
          return `${base}/${path}`;
        };
  
        const next = {
          ...prev,
          id: d.id ?? prev.id,
          name: d.name ?? prev.name,
          email: d.email ?? prev.email,
          role: d.role ?? prev.role,
          status: d.status ?? prev.status,
          // use new image if provided, otherwise keep the previous one
          image: d.image != null ? normalizeImage(d.image) : prev.image,
        };
  
        localStorage.setItem("user", JSON.stringify(next));
  
        toastSuccess(payload.message || "Profile updated successfully");
        await loadProfile();
        setDrawerOpen(false);
      } else {
        throw new Error(payload?.message || "Update failed");
      }
    } catch (err) {
      toastError(err?.message || "Something went wrong");
    }
  };
  
  

  useEffect(() => {
    loadProfile();
  }, []);

  if (loading) return <Spin tip="Loading..." size="large" />;

  return (
    <>
      <Card
        title="My Profile"
        bordered={false}
        extra={
          <Button
            type="primary"
            onClick={() => {
              form.setFieldsValue({
                name: user.name,
                email: user.email,
                password: "", // empty by default
              });
              setDrawerOpen(true);
            }}
          >
            Edit Profile
          </Button>
        }
      >
        <Descriptions bordered column={1} size="middle">
          <Descriptions.Item label="Name">{user?.name}</Descriptions.Item>
          <Descriptions.Item label="Email">{user?.email}</Descriptions.Item>
          <Descriptions.Item label="Role">{user?.role}</Descriptions.Item>
          <Descriptions.Item label="Status">{user?.status}</Descriptions.Item>
          <Descriptions.Item label="Created At">{user?.created_at}</Descriptions.Item>
          <Descriptions.Item label="Updated At">{user?.updated_at}</Descriptions.Item>
        </Descriptions>

        {user?.role === "lawyer" && user.lawyer && (
          <Card title="Lawyer Information" style={{ marginTop: 24 }}>
            <Descriptions column={1}>
              <Descriptions.Item label="CNIC">{user.lawyer.cnic_no}</Descriptions.Item>
              <Descriptions.Item label="Mobile">{user.lawyer.mobile}</Descriptions.Item>
              <Descriptions.Item label="Specialization">{user.lawyer.specialization}</Descriptions.Item>
              <Descriptions.Item label="License No">{user.lawyer.license_number}</Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {user?.role === "firm" && user.firm && (
          <Card title="Firm Information" style={{ marginTop: 24 }}>
            <Descriptions column={1}>
              <Descriptions.Item label="Firm Name">{user.firm.firm_name}</Descriptions.Item>
              <Descriptions.Item label="Phone">{user.firm.phone}</Descriptions.Item>
              <Descriptions.Item label="Address">{user.firm.address}</Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </Card>

      {/* Edit Drawer */}
      <Drawer
        title="Edit Profile"
        open={drawerOpen}
        width={400}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleUpdate}
          autoComplete="off"
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please input your name" }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please input your email" },
              { type: "email", message: "Enter valid email" },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[{ min: 6, message: "Minimum 6 characters" }]}
          >
            <Input.Password placeholder="New password (optional)" />
          </Form.Item>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button style={{ marginRight: 8 }} onClick={() => setDrawerOpen(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
          </div>
        </Form>
      </Drawer>
    </>
  );
}
